import React, { useEffect, useState } from "react";
import {
  View,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  KeyboardAvoidingView,
  ActivityIndicator} from "react-native";
import AntDesign from "@expo/vector-icons/AntDesign";
import { statesArray } from "./statesArray";
import { ApiService } from "../../../service/ApiService";
import { SafeAreaView } from "react-native-safe-area-context";
import commonStyles from "../../../styles/commonStyles";
import Toast from "react-native-toast-message";
import AsyncStorage from "@react-native-async-storage/async-storage";
import AddressView from "./AddressView";
import Feather from "@expo/vector-icons/Feather";
import CommonDropdown from "../../../components/Dropdown";
import { IUpdatedUserData, IUserTypes } from "../../../types/IUserTypes";
import PersonalDetailsStyle from "../PersonalDetails/PersonalDetailsStyle";
import { fonts } from "../../../styles/theme";
import AppText from "../../../components/AppText";
import AppTextInput from "../../../components/AppTextInput";

type AddressProps = {
  navigation: {
    navigate: (screen: string) => void;
    goBack: () => void;
  };
};

type FormData = {
  street1: string;
  street2: string;
  state: string;
  city: string;
  zipCode: string;
};

const AddressPage: React.FC<AddressProps> = ({ navigation }) => {
  const [isEnabled, setIsEnabled] = useState(false);
  const [isEditable, setIsEditable] = useState(false);
  const [userData, setUserData] = useState<IUserTypes>();
  const [loading, setLoading] = useState<boolean>(false);
  const [formData, setFormData] = useState<FormData>({
    street1: "",
    street2: "",
    state: "",
    city: "",
    zipCode: ""});

  const fetchUserData = async () => {
    try {
      const apiResponse = await AsyncStorage.getItem("apiResponse");
      const parsedData = JSON.parse(apiResponse || "{}");
      setUserData(parsedData);
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  };

  useEffect(() => {
    if (userData) {
      setFormData({
        street1: userData.address?.street1 || "",
        street2: userData.address?.street2 || "",
        state: userData.address?.state || "",
        city: userData.address?.city || "",
        zipCode: userData.address?.zipCode || ""});
    }
  }, [userData]);
  useEffect(() => {
    fetchUserData();
  }, []);

  const [isFocused, setIsFocused] = useState<Record<string, boolean>>({});

  const handleFocus = (field: string) =>
    setIsFocused({ ...isFocused, [field]: true });
  const handleBlur = (field: string) =>
    setIsFocused({ ...isFocused, [field]: false });
  const handleChange = (field: keyof FormData, value: string) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSubmit = async () => {
    if (!userData) return;
    // console.log("userdata", userData);
    setLoading(true);
    const updatedUserData: IUpdatedUserData = {
      id: userData.id,
      firstName: userData.firstName,
      lastName: userData.lastName,
      middleName: userData.middleName,
      gender: userData.gender,
      employmentStatus: userData.employmentStatus,
      householdIncome: userData.householdIncome,
      phoneNumber: userData.phoneNumber,
      maritalStatus: userData.maritalStatus,
      accreditedInvestor: userData.accreditedInvestor,
      immigrationStatus: userData.immigrationStatus,
      address: {
        street1: formData.street1,
        street2: formData.street2,
        city: formData.city,
        state: formData.state,
        zipCode: formData.zipCode},
      bankDetail: userData.bankDetail};

    if (
      !updatedUserData.householdIncome ||
      !updatedUserData.phoneNumber ||
      !updatedUserData.gender ||
      !updatedUserData.maritalStatus ||
      !updatedUserData.employmentStatus
    ) {
      Toast.show({
        type: "error",
        text1: "Please fill all the required fields"});
      setLoading(false);
      navigation.navigate("PersonalDetails");
    } else {
      try {
        const apiUrl = `portal/partner/${userData.id}`;
        const response = await ApiService.put<IUserTypes>(
          apiUrl,
          updatedUserData
        );

        updatedUserData.userRoles = userData.userRoles;
        updatedUserData.bankDetail = userData.bankDetail;
        updatedUserData.email = userData.email;
        updatedUserData.partnerSpouse = userData.partnerSpouse;
        updatedUserData.pendingSpouseRequest = userData.pendingSpouseRequest;
        await AsyncStorage.setItem(
          "apiResponse",
          JSON.stringify(updatedUserData)
        );
        Toast.show({
          type: "success",
          text1: "Data updated successfully!"});

        setLoading(false);
        setUserData(response.data);
        setIsEditable(false);
        setIsEnabled(false);
      } catch (error) {
        console.error("Error updating user data:", error);
        Toast.show({
          type: "error",
          text1: "Failed to update data. Please try again."});
        setLoading(false);
      }
    }
  };

  const onEdit = () => {
    setIsEditable(!isEditable);
    setIsEnabled(!isEnabled);
  };

  return (
    <SafeAreaView
      edges={["bottom", "left", "right"]}
      style={[PersonalDetailsStyle.container, commonStyles.screenBottomGap]}
    >
      <View style={[PersonalDetailsStyle.subHeaderContainer]}>
        <TouchableOpacity
          testID="backButton"
          style={[PersonalDetailsStyle.subHeaderContainer]}
          onPress={navigation.goBack}
        >
          <AntDesign name="left" size={24} color="black" />
          <AppText style={commonStyles.subHeader}>Address</AppText>
        </TouchableOpacity>
        <View style={styles.switchContainer}>
          <TouchableOpacity onPress={onEdit} testID="editButton">
            <Feather
              name="edit"
              size={24}
              color="black"
              style={commonStyles.mr8}
            />
          </TouchableOpacity>
          {/* <Switch
            value={isEnabled}
            onValueChange={(value) => {
              setIsEnabled(value);
            }}
          /> */}
        </View>
      </View>
      {!isEditable && userData ? (
        <AddressView userData={userData} />
      ) : (
        <KeyboardAvoidingView style={{ flex: 1 }} behavior="padding">
          <ScrollView
            contentContainerStyle={PersonalDetailsStyle.scrollContainer}
            keyboardShouldPersistTaps="handled"
          >
            {/* <View
              style={[
                commonStyles.subContainer,
                commonStyles.p16,
                commonStyles.mlar5,
              ]}
            > */}
            <View>
              <AppText style={styles.label} testID="street1Input">
                Address 1
              </AppText>
              <AppTextInput
                style={[
                  styles.input,
                  !isEditable && styles.disabledInput,
                  { borderColor: isFocused.street1 ? "#4e2ea2" : "#ddd" },
                ]}
                placeholder="Enter Address 1"
                placeholderTextColor="#979797"
                value={formData.street1}
                onFocus={() => handleFocus("street1")}
                onBlur={() => handleBlur("street1")}
                editable={isEditable}
                onChangeText={(value) => handleChange("street1", value)}
              />
            </View>
            <View>
              <AppText style={styles.label}>Address 2 (optional)</AppText>
              <AppTextInput
                style={[
                  styles.input,
                  !isEditable && styles.disabledInput,
                  { borderColor: isFocused.street2 ? "#4e2ea2" : "#ddd" },
                ]}
                placeholder="Enter Address 2"
                placeholderTextColor="#979797"
                value={formData.street2}
                onFocus={() => handleFocus("street2")}
                onBlur={() => handleBlur("street2")}
                editable={isEditable}
                onChangeText={(value) => handleChange("street2", value)}
              />
            </View>
            <View>
              <AppText style={styles.label} testID="cityInput">
                City
              </AppText>
              <AppTextInput
                style={[
                  styles.input,
                  !isEditable && styles.disabledInput,
                  { borderColor: isFocused.city ? "#4e2ea2" : "#ddd" },
                ]}
                placeholder="Enter City"
                placeholderTextColor="#979797"
                value={formData.city}
                editable={isEditable}
                onFocus={() => handleFocus("city")}
                onBlur={() => handleBlur("city")}
                onChangeText={(value) => handleChange("city", value)}
              />
            </View>
            <View testID="stateDropdown" style={{ marginBottom: 10 }}>
              <CommonDropdown
                title="State"
                isRequired={false}
                data={statesArray.map((state) => ({
                  title: state.name,
                  value: state.value}))}
                value={formData.state}
                onChange={(item) => {
                  handleChange("state", item.value);
                }}
                placeholder="Select state"
                isEditable={isEditable}
              />
            </View>

            <View>
              <AppText style={styles.label}>Zip Code</AppText>
              <AppTextInput
                style={[
                  styles.input,
                  !isEditable && styles.disabledInput,
                  { borderColor: isFocused.zipCode ? "#4e2ea2" : "#ddd" },
                ]}
                testID="zipCodeInput"
                placeholder="Enter Zip Code"
                placeholderTextColor="#979797"
                value={formData.zipCode}
                editable={isEditable}
                onFocus={() => handleFocus("zipCode")}
                onBlur={() => handleBlur("zipCode")}
                onChangeText={(value) => handleChange("zipCode", value)}
              />
            </View>

            {isEnabled && (
              <TouchableOpacity
                style={styles.button}
                onPress={handleSubmit}
                disabled={loading}
              >
                {loading ? (
                  <ActivityIndicator size="small" color="#fff" />
                ) : (
                  <AppText style={styles.buttonText} testID="submitButton">
                    Update
                  </AppText>
                )}
              </TouchableOpacity>
            )}
            {/* </View> */}
          </ScrollView>
        </KeyboardAvoidingView>
      )}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    backgroundColor: "#fff"},
  backButtonContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 20},
  backButtonText: {
    fontSize: 18,
    fontWeight: "600",
    marginLeft: 10,
    color: "#333"},
  scrollContainer: {
    paddingBottom: 20,
    paddingTop: 20},
  label: {
    marginBottom: 8,
    fontSize: 12,
    color: "#979797",
    fontWeight: "600",
    fontFamily: fonts.regular},
  input: {
    height: 45,
    borderWidth: 1,
    borderRadius: 15,
    paddingHorizontal: 12,
    fontSize: 14,
    color: "#343434",
    marginBottom: 20,
    fontWeight: "600",
    // backgroundColor: "#f9f9f9",
    borderColor: "#CBCBCB",
    fontFamily: fonts.regular},

  placeholderText: {
    fontSize: 14,
    color: "#979797",
    fontWeight: "500",
    fontFamily: fonts.regular},

  redStar: {
    color: "#343434"},
  button: {
    backgroundColor: "#3629B7",
    paddingVertical: 15,
    borderRadius: 10,
    alignItems: "center",
    marginTop: 20},
  buttonText: {
    color: "#fff",
    fontSize: 16,
    fontWeight: "600",
    fontFamily: fonts.regular},
  switchContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginLeft: "auto"},
  disabledInput: {
    backgroundColor: "#f0f0f0",
    color: "#999"}});

export default AddressPage;
